import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { MaintenanceSession as MaintenanceSessionData } from '../../types/maintenance';
import { ConfirmationModal } from '../shared/ConfirmationModal';

interface MaintenanceSessionProps {
  sessionId: string;
  onComplete?: () => void;
}

interface MachineInfo {
  id: string;
  code: string;
  location: string;
  status: string;
}

export const MaintenanceSession: React.FC<MaintenanceSessionProps> = ({
  sessionId,
  onComplete,
}) => {
  const navigate = useNavigate();
  const [session, setSession] = useState<MaintenanceSessionData | null>(null);
  const [machine, setMachine] = useState<MachineInfo | null>(null);
  const [notes, setNotes] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isEnding, setIsEnding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEndConfirmation, setShowEndConfirmation] = useState(false);

  useEffect(() => {
    loadSession();
  }, [sessionId]);

  useEffect(() => {
    if (!session || session.status === 'completed') return;

    // Update elapsed time every second
    const interval = setInterval(() => {
      const startTime = new Date(session.startTime).getTime();
      const now = Date.now();
      const elapsed = Math.floor((now - startTime) / 1000);
      setElapsedTime(elapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [session]);

  const loadSession = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Load session details
      const sessionResponse = await api.get<MaintenanceSessionData>(
        `/maintenance-sessions/${sessionId}`
      );
      setSession(sessionResponse.data);
      setNotes(sessionResponse.data.notes || '');

      // Load machine details
      const machineResponse = await api.get<MachineInfo>(
        `/machines/${sessionResponse.data.machineId}`
      );
      setMachine(machineResponse.data);

      // Calculate initial elapsed time
      if (sessionResponse.data.status === 'active') {
        const startTime = new Date(sessionResponse.data.startTime).getTime();
        const now = Date.now();
        const elapsed = Math.floor((now - startTime) / 1000);
        setElapsedTime(elapsed);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao carregar sessão de manutenção');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndMaintenance = async () => {
    if (!session) return;
    setShowEndConfirmation(true);
  };

  const confirmEndMaintenance = async () => {
    setShowEndConfirmation(false);
    setIsEnding(true);
    setError(null);

    try {
      await api.put(`/maintenance-sessions/${sessionId}/end`, {
        notes: notes.trim() || undefined,
      });

      // Call onComplete callback if provided
      if (onComplete) {
        onComplete();
      } else {
        // Navigate back to schedule
        navigate('/my-schedule');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao finalizar manutenção');
    } finally {
      setIsEnding(false);
    }
  };

  const formatElapsedTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (error && !session) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-lg font-medium text-red-800 mb-2">Erro ao Carregar Sessão</h3>
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={() => navigate('/my-schedule')}
                className="mt-4 inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Voltar para Agenda
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!session || !machine) {
    return null;
  }

  const isCompleted = session.status === 'completed';

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900">
            {isCompleted ? 'Manutenção Concluída' : 'Manutenção em Andamento'}
          </h2>
          <span
            className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
              isCompleted
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}
          >
            {isCompleted ? 'Concluída' : 'Ativa'}
          </span>
        </div>

        {/* Machine Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <h3 className="text-sm font-medium text-gray-600 mb-3">Máquina</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Código:</span>
              <span className="font-mono font-bold text-lg text-orange-600">
                {machine.code}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Localização:</span>
              <span className="font-medium text-gray-900">{machine.location}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Status:</span>
              <span
                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  machine.status === 'maintenance'
                    ? 'bg-yellow-100 text-yellow-800'
                    : machine.status === 'online'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {machine.status === 'maintenance' ? 'Em Manutenção' : machine.status}
              </span>
            </div>
          </div>
        </div>

        {/* Time Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium text-blue-900">Início</span>
            </div>
            <p className="text-lg font-bold text-blue-900">
              {formatDateTime(session.startTime)}
            </p>
          </div>

          <div className={`rounded-lg p-4 ${isCompleted ? 'bg-green-50' : 'bg-orange-50'}`}>
            <div className="flex items-center mb-2">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className={`text-sm font-medium ${isCompleted ? 'text-green-900' : 'text-orange-900'}`}>
                {isCompleted ? 'Duração' : 'Tempo Decorrido'}
              </span>
            </div>
            <p className={`text-2xl font-bold font-mono ${isCompleted ? 'text-green-900' : 'text-orange-900'}`}>
              {isCompleted && session.duration
                ? `${session.duration} min`
                : formatElapsedTime(elapsedTime)}
            </p>
          </div>
        </div>

        {isCompleted && session.endTime && (
          <div className="bg-green-50 rounded-lg p-4 mb-4">
            <div className="flex items-center mb-2">
              <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium text-green-900">Finalização</span>
            </div>
            <p className="text-lg font-bold text-green-900">
              {formatDateTime(session.endTime)}
            </p>
          </div>
        )}
      </div>

      {/* Notes Section */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Observações</h3>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={isCompleted || isEnding}
          placeholder="Adicione observações sobre a manutenção realizada..."
          rows={6}
          className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
        />
        <p className="text-xs text-gray-500 mt-2">
          {notes.length} caracteres
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {!isCompleted && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex gap-4">
            <button
              onClick={handleEndMaintenance}
              disabled={isEnding}
              className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              {isEnding ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Finalizando...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Finalizar Manutenção
                </>
              )}
            </button>

            <button
              onClick={() => navigate('/my-schedule')}
              disabled={isEnding}
              className="px-6 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancelar
            </button>
          </div>

          <p className="text-xs text-gray-500 mt-4 text-center">
            Ao finalizar, a máquina voltará ao status online e a sessão será registrada no histórico.
          </p>
        </div>
      )}

      {isCompleted && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <button
            onClick={() => navigate('/my-schedule')}
            className="w-full inline-flex items-center justify-center px-6 py-3 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors shadow-sm"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Voltar para Agenda
          </button>
        </div>
      )}
      
      {/* End Maintenance Confirmation Modal */}
      <ConfirmationModal
        isOpen={showEndConfirmation}
        onClose={() => setShowEndConfirmation(false)}
        onConfirm={confirmEndMaintenance}
        title="Finalizar Manutenção"
        message="Tem certeza que deseja finalizar a manutenção? A máquina voltará ao status online e a sessão será registrada no histórico."
        confirmText="Finalizar"
        cancelText="Cancelar"
        type="warning"
      />
    </div>
  );
};
